/**
 * Useful for adding a prefix and suffix to all keys of an object while maintaining camel case.
 *
 * @example
 * interface Analytics {id: number; name: string};
 * type SourceAnalytics = WithKeyPrefixSuffix<Analytics, 'Source', 'ForSchema'>;
 * // {SourceIdForSchema: number, SourceNameForSchema: string}
 * type SuffixAnalytics = WithKeyPrefixSuffix<Analytics, '', 'Suffix'>;
 * // {idSuffix: number, nameSuffix: string}
 */
export type WithKeyPrefixSuffix<T extends object, Prefix extends string, Suffix extends string> = {
  [K in keyof T as K extends string
    ? `${Prefix}${Prefix extends '' ? K : Capitalize<K>}${Suffix}`
    : never]: T[K];
};

/**
 * Returns a copy of object, with prefix and suffix added to all keys.
 * addPrefixSuffixToObjectKeys({id: 1, name: 'foo'}, 'pre', 'Suffix') equals {preIdSuffix: 1, preNameSuffix: 'foo'}
 * addPrefixSuffixToObjectKeys({id: 1, name: 'foo'}, '', 'Suffix') equals {idSuffix: 1, nameSuffix: 'foo'}
 */
export default function addPrefixSuffixToObjectKeys<T extends object, P extends string, S extends string>(
  object: T,
  prefix: P,
  suffix: S
): WithKeyPrefixSuffix<T, P, S> {
  return Object.fromEntries(
    Object.entries(object).map(([key, value]) => [
      `${prefix}${prefix ? key[0].toUpperCase() + key.slice(1) : key}${suffix}`,
      value,
    ])
  ) as WithKeyPrefixSuffix<T, P, S>;
}
